import { useId, useLayoutEffect, useRef, useState } from "react";
import highlightClick from "../EarTrainerPage/highlightClick";
import classes from "./FretboardChart.module.css";

const FretboardChart = ({ image, stringCount, maxFret, onClick, feedbackContext,
  trainingMode, confirmationDuration = 300 }) => {
  const grid = useRef(null);
  const imageRef = useRef(null);
  const marker = useRef(null);
  const id = useId();
  const [location, setLocation] = useState({ string: 0, fret: 0 });
  const string = Math.min(location.string, stringCount - 1);
  const fret = Math.min(location.fret, maxFret - 1);
  const cellId = `${id}-${string}-${fret}`;
  useLayoutEffect(() => () => { marker.current?.(); marker.current = null; },
    [feedbackContext, trainingMode, stringCount, maxFret]);

  const submit = (string, fret) => {
    const result = onClick(string, fret);
    if (!result) return;
    const rect = imageRef.current.getBoundingClientRect();
    const origin = grid.current.getBoundingClientRect();
    marker.current?.();
    marker.current = highlightClick(
      (fret + 1) * rect.width / (maxFret + 1) + rect.left - origin.left,
      (stringCount - string) * rect.height / (stringCount + 1) + rect.top - origin.top,
      { container: grid.current, result, size: 24,
        color: result === "correct" ? "green" : result === "incorrect" ? "red" : "#2563eb",
        duration: result === "correct" ? confirmationDuration : 500 });
  };
  const clickHandler = event => {
    const x = event.nativeEvent.offsetX, y = event.nativeEvent.offsetY;
    const fretInc = event.currentTarget.clientWidth / (maxFret + 1);
    const stringInc = event.currentTarget.clientHeight / (stringCount + 1);
    const fret = Array.from({ length: maxFret }, (_, n) => n)
      .find(n => x > (n + 0.5) * fretInc && x < (n + 1.5) * fretInc);
    const row = Array.from({ length: stringCount }, (_, n) => n)
      .find(n => y > (n + 0.5) * stringInc && y < (n + 1.5) * stringInc);
    if (fret === undefined || row === undefined) return;
    const string = stringCount - row - 1;
    setLocation({ string, fret });
    grid.current.focus({ preventScroll: true });
    submit(string, fret);
  };
  const keyHandler = event => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const moves = {
      ArrowLeft: { string, fret: Math.max(0, fret - 1) },
      ArrowRight: { string, fret: Math.min(maxFret - 1, fret + 1) },
      ArrowUp: { string: Math.min(stringCount - 1, string + 1), fret },
      ArrowDown: { string: Math.max(0, string - 1), fret },
    };
    if (moves[event.key]) {
      event.preventDefault(); setLocation(moves[event.key]);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!event.repeat) submit(string, fret);
    }
  };

  return (
    <div ref={grid} className={classes.chart} tabIndex={0} role="grid"
      aria-label="Practice fretboard" aria-rowcount={stringCount} aria-colcount={maxFret}
      aria-activedescendant={cellId} aria-describedby={`${id}-help`} onKeyDown={keyHandler}>
      <img ref={imageRef} id="fretboardTrainerChart" draggable="false"
        src={`data:image/svg+xml;utf8,${encodeURIComponent(image)}`} alt="" aria-hidden="true" onClick={clickHandler} />
      <span className={classes.cursor} data-keyboard-cursor aria-hidden="true" style={{
        left: `${(fret + 1) * 100 / (maxFret + 1)}%`,
        top: `${(stringCount - string) * 100 / (stringCount + 1)}%`,
      }} />
      <div className={classes.srOnly} role="row" aria-rowindex={stringCount - string}>
        <span role="gridcell" id={cellId} aria-colindex={fret + 1}
          aria-label={`String ${stringCount - string} of ${stringCount}, fret ${fret}${fret === 0 ? " (open)" : ""}`} />
      </div>
      <span id={`${id}-help`} className={classes.srOnly}>
        Use arrow keys to move, Enter or Space to select a position. While stopped, selection plays its note when Sound is On.
        String 1 is the top string, usually the highest-pitched string. Fret 0 is the open string.
      </span>
    </div>
  );
};

export default FretboardChart;
